import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useAuth, useFeature } from '../../context/AuthContext';
import { toastSuccess, toastError, errorMessage } from '../../lib/toast';
import { REQUEST_STATUS_TONE } from '../../lib/requestStatus';
import { formatDateTime } from '../../lib/formatDate';
import { ToolIcon } from '../home/tools/ToolTile';
import './requests.css';

/**
 * Merged history of the user's tool requests:
 *   - Access requests for catalog tools (tool_access_requests/mine).
 *   - "Propose new tool" requests (tool_requests, filtered to own + non-team).
 *
 * Both sources are normalised into a common row shape so the UI can render
 * a single sortable, filterable table.
 */

const KIND_LABEL = {
  access: 'Access request',
  new_tool: 'New tool',
};
// Shared .at-pill variant per status; labels come from REQUEST_STATUS_TONE so a
// request reads the same here as on the home dashboard.
const STATUS_PILL = {
  pending: 'warning',
  pending_manager: 'warning',
  pending_admin: 'info',
  approved: 'success',
  rejected: 'danger',
};
const KIND_PILL = { access: 'info', new_tool: 'neutral' };
const PENDING_STATUSES = new Set(['pending', 'pending_manager', 'pending_admin']);

export default function MyRequestsTab() {
  const { user: currentUser, featuresLoaded } = useAuth();
  const proposalsOn = useFeature('tool_proposal_approval');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Proposals are a second, independent source: when only they fail, the
  // access history still shows, with a notice that proposals are missing.
  const [proposalsFailed, setProposalsFailed] = useState(false);
  const [access, setAccess] = useState([]);
  const [newTool, setNewTool] = useState([]);
  const [kindFilter, setKindFilter] = useState('all'); // all | access | new_tool
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | approved | rejected

  const load = async () => {
    setLoading(true);
    setError('');
    setProposalsFailed(false);
    try {
      // Fetch both independently. New-tool proposals live behind the
      // tool_proposal_approval feature flag: when it's off the route 403s
      // feature_disabled, so don't call it at all. A proposals failure must NOT
      // wipe out the access-request history; it is flagged instead.
      const accessP = axios
        .get(`${API_BASE_URL}/api/tool_access_requests/mine`, { withCredentials: true })
        .then((a) => (Array.isArray(a.data) ? a.data : []));
      const newP = !proposalsOn
        ? Promise.resolve({ rows: [], failed: false })
        : axios
          .get(`${API_BASE_URL}/api/tool_requests`, { withCredentials: true })
          .then((n) => ({ rows: Array.isArray(n.data) ? n.data : [], failed: false }))
          .catch((err) => (err?.response?.data?.error === 'feature_disabled'
            ? { rows: [], failed: false }
            : { rows: [], failed: true }));
      const [a, n] = await Promise.all([accessP, newP]);
      setAccess(a);
      // /api/tool_requests returns own+team for dept managers and *every*
      // request for admins. This tab is the user's personal history, so
      // hard-filter to rows the current user submitted regardless of role.
      const uid = currentUser?.id;
      setNewTool(n.rows.filter((r) => uid && r.user_id === uid));
      setProposalsFailed(n.failed);
    } catch (err) {
      setError(errorMessage(err, "Couldn't load your requests. Try again."));
    } finally {
      setLoading(false);
    }
  };

  // Wait for the flags: they start fail-open (enabled) and arrive separately
  // from /api/me, so loading earlier fetched once with the wrong flag value and
  // then again when it settled.
  useEffect(() => {
    if (currentUser?.id && featuresLoaded) load();
    // `load` is rebuilt every render; refetch only on these inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, proposalsOn, featuresLoaded]);

  // If proposals get disabled while the New-tool filter is active, reset to All
  // so the user isn't stuck on an empty, hidden filter.
  useEffect(() => {
    if (!proposalsOn && kindFilter === 'new_tool') setKindFilter('all');
  }, [proposalsOn, kindFilter]);

  const rows = useMemo(() => {
    const normalisedAccess = access.map((r) => ({
      key: `a-${r.id}`,
      id: r.id,
      kind: 'access',
      title: r.tool_name,
      icon_url: r.tool_icon_url,
      detail: r.reason,
      status: r.status,
      admin_notes: r.admin_notes,
      auto_approved: r.auto_approved,
      created_at: r.created_at,
      resolved_at: r.resolved_at,
    }));
    const normalisedNew = newTool.map((r) => ({
      key: `n-${r.id}`,
      id: r.id,
      kind: 'new_tool',
      title: r.name,
      icon_url: r.icon_url,
      detail: r.description || r.link,
      status: r.status,
      admin_notes: r.admin_notes,
      manager_notes: r.manager_notes,
      approvers: r.approvers,
      auto_approved: false,
      created_at: r.created_at,
      resolved_at: r.resolved_at,
    }));
    return [...normalisedAccess, ...normalisedNew].sort((x, y) => {
      const ax = new Date(x.created_at).getTime() || 0;
      const ay = new Date(y.created_at).getTime() || 0;
      return ay - ax;
    });
  }, [access, newTool]);

  const counts = useMemo(() => {
    const out = { all: rows.length, access: 0, new_tool: 0, pending: 0, approved: 0, rejected: 0 };
    for (const r of rows) {
      out[r.kind]++;
      out[r.status] = (out[r.status] || 0) + 1;
    }
    return out;
  }, [rows]);

  const visible = useMemo(() => rows.filter((r) => {
    if (kindFilter !== 'all' && r.kind !== kindFilter) return false;
    if (statusFilter === 'all') return true;
    // The 'pending' filter bucket matches all in-flight statuses for both kinds.
    if (statusFilter === 'pending') return PENDING_STATUSES.has(r.status);
    return r.status === statusFilter;
  }), [rows, kindFilter, statusFilter]);

  // Access requests are cancellable while status === 'pending'.
  // New-tool proposals are cancellable while status === 'pending_manager'
  // (manager hasn't acted yet); once a manager approves/rejects it can't be cancelled.
  const isCancellable = (row) =>
    row.kind === 'access' ? row.status === 'pending' : row.status === 'pending_manager';

  const cancelRequest = async (row) => {
    if (!isCancellable(row)) return;
    if (!window.confirm(`Cancel your pending request for "${row.title}"?`)) return;
    const url = row.kind === 'access'
      ? `${API_BASE_URL}/api/tool_access_requests/${row.id}`
      : `${API_BASE_URL}/api/tool_requests/${row.id}`;
    try {
      await axios.delete(url, { withCredentials: true });
      toastSuccess('Request cancelled.');
      await load();
    } catch (err) {
      toastError(errorMessage(err, "Couldn't cancel your request. Try again."));
    }
  };

  if (loading) {
    return <p className="rq-loading" role="status" aria-busy="true">Loading your requests…</p>;
  }
  if (error) {
    return (
      <div className="rq-error" role="alert">
        <span>{error}</span>
        <button type="button" className="btn btn-secondary btn-small" onClick={load}>Try again</button>
      </div>
    );
  }
  const notice = proposalsFailed && (
    <p className="rq-notice" role="alert">
      Couldn't load your tool proposals, so only access requests are listed. Reload to try again.
    </p>
  );
  if (rows.length === 0) {
    return (
      <>
        {notice}
        <div className="rq-empty">
          {proposalsOn ? (
            <p>You haven't submitted any requests yet. Use <strong>Request access</strong> for catalog tools, or <strong>Propose new tool</strong> for new ones.</p>
          ) : (
            <p>You haven't submitted any requests yet. Use <strong>Request access</strong> to ask for a catalog tool.</p>
          )}
        </div>
      </>
    );
  }

  const pendingCount = (counts.pending || 0) + (counts.pending_manager || 0) + (counts.pending_admin || 0);

  return (
    <div>
      {notice}
      <div className="rq-filters">
        {proposalsOn && (
          <FilterChips
            label="Kind"
            options={[
              ['all', `All (${counts.all})`],
              ['access', `Access (${counts.access})`],
              ['new_tool', `New tool (${counts.new_tool})`],
            ]}
            value={kindFilter}
            onChange={setKindFilter}
          />
        )}
        <FilterChips
          label="Status"
          options={[
            ['all', `All (${counts.all})`],
            ['pending', `Pending (${pendingCount})`],
            ['approved', `Added (${counts.approved || 0})`],
            ['rejected', `Rejected (${counts.rejected || 0})`],
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </div>

      <div className="admin-table-wrap">
        {/* min-width: on a phone the six columns crushed to a word per line;
            the wrap scrolls sideways instead. */}
        <table className="admin-table rq-mine-table">
          <thead>
            <tr>
              <th>Tool</th>
              {proposalsOn && <th>Kind</th>}
              <th>Details</th>
              <th style={{ width: 160 }}>Status</th>
              <th className="rq-nowrap">Submitted</th>
              <th style={{ width: 110, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const tone = REQUEST_STATUS_TONE[r.status];
              return (
                <tr key={r.key}>
                  <td>
                    <div className="rq-tool-cell">
                      <span className="rq-tool-icon"><ToolIcon tool={{ name: r.title, icon_url: r.icon_url }} /></span>
                      <span className="at-primary">{r.title}</span>
                    </div>
                  </td>
                  {proposalsOn && (
                    <td>
                      <span className={`at-pill at-pill--${KIND_PILL[r.kind]} rq-nowrap`}>
                        {KIND_LABEL[r.kind]}
                      </span>
                    </td>
                  )}
                  <td className="at-muted" style={{ maxWidth: 320, whiteSpace: 'normal' }}>
                    {r.detail || '—'}
                    {r.admin_notes && (
                      <div className="rq-note">
                        <strong>Admin:</strong> {r.admin_notes}
                      </div>
                    )}
                    {r.kind === 'new_tool' && r.status === 'rejected' && r.manager_notes && (
                      <div className="rq-note">
                        <strong>Manager:</strong> {r.manager_notes}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`at-pill at-pill--${STATUS_PILL[r.status] || 'neutral'} rq-nowrap`}>
                      {tone ? tone.label : 'Unknown'}
                    </span>
                    {r.auto_approved && <div className="rq-sub">auto-approved</div>}
                    {r.approvers && r.approvers.length > 0 && (r.status === 'pending_manager' || r.status === 'pending_admin') && (
                      <div className="rq-sub">
                        Approver{r.approvers.length > 1 ? 's' : ''}: {r.approvers.map((a) => (a && a.name) || a).join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="at-muted rq-nowrap">
                    {formatDateTime(r.created_at)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {isCancellable(r) ? (
                      <button
                        type="button"
                        className="btn btn-small btn-danger-outline"
                        onClick={() => cancelRequest(r)}
                        aria-label={`Cancel request for ${r.title}`}
                      >
                        Cancel
                      </button>
                    ) : (
                      <span className="at-muted" aria-hidden="true">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={proposalsOn ? 6 : 5} style={{ textAlign: 'center', padding: 32 }} className="at-muted">
                  No requests match the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterChips({ label, options, value, onChange }) {
  return (
    <div className="rq-chip-group" role="group" aria-label={label}>
      <span className="rq-chip-label" aria-hidden="true">{label}</span>
      {options.map(([k, l]) => (
        <button
          key={k}
          type="button"
          className={`rq-chip${value === k ? ' is-active' : ''}`}
          aria-pressed={value === k}
          onClick={() => onChange(k)}
        >
          {l}
        </button>
      ))}
    </div>
  );
}
